import L from 'leaflet';
import React from 'react';
import { Marker, Popup } from 'react-leaflet';
import { NavLink } from 'react-router-dom';

import _ from 'lodash';
import { useTranslation } from 'react-i18next';

import PropTypes from 'prop-types';
import ReadingsHelpers from 'utils/ReadingsHelpers';

import Healthy from 'assets/probe_associated.png';
import Warning from 'assets/probe_active_warning.png';
import Error from 'assets/probe_active_danger.png';
import { useFields, useOrganizations, useEvents } from 'hooks';
import { DashboardHelpers } from 'utils';
import { Grid, makeStyles } from '@material-ui/core';
import moment from 'moment';

const iconTeralyticHealthy = new L.Icon({
  iconUrl: Healthy,
  iconRetinaUrl: Healthy,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
  iconSize: [50, 50],
  className: 'leaflet-tl-icon',
});

const iconTeralyticWarning = new L.Icon({
  iconUrl: Warning,
  iconRetinaUrl: Warning,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
  iconSize: [50, 50],
  className: 'leaflet-tl-icon-warn',
});

const iconTeralyticError = new L.Icon({
  iconUrl: Error,
  iconRetinaUrl: Error,
  shadowUrl: null,
  shadowSize: null,
  shadowAnchor: null,
  iconSize: [50, 50],
  className: 'leaflet-tl-icon-error',
});

const useStyles = makeStyles(() => ({
  popup: {
    width: '250px',
  },
}));

export default function ProbeMarker({ probe, telemetry, events, position, ...props }) {
  const { t } = useTranslation();
  const { fields } = useFields();
  const { organizations } = useOrganizations();
  const { fetchEvents, setFilter, filters } = useEvents();
  const tips = [];
  const classes = useStyles();

  let renderIcon = iconTeralyticHealthy;

  const errorEvent = _.find(events, (event) => {
    return event.type === 'error' && moment().diff(moment(event.created_at), 'hours') < 48;
  });
  const warnEvent = _.find(events, (event) => {
    return event.type === 'warn' && moment().diff(moment(event.created_at), 'hours') < 48;
  });

  const setSearchFilterOfEvents = () => {
    const searchText = errorEvent?.data?.device_id;
    if (searchText) {
      setFilter({ search: searchText });
      const allFilters = Object.assign({}, filters, { search: searchText });
      fetchEvents(allFilters);
    }
  };

  if (errorEvent) {
    renderIcon = iconTeralyticError;
    tips.push(
      <div key={errorEvent.id}>
        <span>
          <div>
            <b>{t('insight')} - Error:</b>
            <br></br>
            <NavLink to={'/events/'} activeClassName="active" onClick={setSearchFilterOfEvents}>
              {errorEvent?.data?.insight}
            </NavLink>
          </div>
        </span>
      </div>
    );
  } else if (warnEvent) {
    renderIcon = iconTeralyticWarning;
    tips.push(
      <div key={warnEvent.id}>
        <span>
          <div>
            <b>{t('insight')} - Warning:</b>
            <br></br>
            <div>{warnEvent.data.insight}</div>
          </div>
        </span>
      </div>
    );
  }

  const buildLocationLink = () => {
    if (typeof position?.lat === 'number' && typeof position?.lon === 'number') {
      const lat = position.lat.toString();
      const long = position.lon.toString();
      return 'https://www.google.com/maps/place/' + lat + ',' + long;
    }
  };

  const probeFieldName = () => {
    let fieldName = 'Undefined Field';
    const field = _.find(fields, { id: probe.field_id });
    if (field) {
      fieldName = field.name;
    } else {
      const org = _.find(organizations, { id: probe.organization_id });
      if (org) {
        fieldName = org.name;
      }
    }

    return fieldName;
  };
  let probeInfo;
  if (telemetry) {
    _.forEach(telemetry, (value, reading) => {
      const r = ReadingsHelpers.readings[reading];
      if (r) {
        tips.push(
          <div key={`${probe.hw_addr}${reading}`} style={{ display: 'grid-item', paddingLeft: 10 }}>
            <span style={{ width: 260, paddingTop: 15 }}>
              <div className="tooltip-field">
                <b style={{ paddingLeft: 10, marginRight: 10, width: 125 }}>{t(reading)}</b>
                <div className="tooltip-value">{`${ReadingsHelpers.displayReading(reading, value, '', true)}`}</div>
              </div>
            </span>
          </div>
        );
      }
    });
  }
  const name = DashboardHelpers.trimHWAddress(probe.hw_address);
  const field = probeFieldName();
  const locationLink = buildLocationLink();
  probeInfo = (
    <Popup zIndex={75} offset={[-9, -10]} key={probe.id} position={position} className={classes.popup}>
      <Grid container item xs={6} spacing={1}>
        <Grid container item xs={12} className="tooltip-header">
          <Grid item xs={10}>
            {`Probe: ${name}`}
          </Grid>
          <Grid item xs={2}>
            {probe.rssi !== undefined ? DashboardHelpers.getIconForRSSIValue(probe.rssi) : ''}
          </Grid>
        </Grid>
        <Grid item xs={12} className="tooltip-field">{`${
          fields && fields.length > 0 ? 'Field' : 'Organization'
        }: ${field}`}</Grid>
        <Grid item xs={12} style={{ display: 'grid-container' }}>
          {tips}
        </Grid>
        {locationLink && (
          <Grid item xs={12} className="tooltip-field">
            <a href={locationLink} target="_blank" rel="noreferrer">
              {t('open_location')}
            </a>
          </Grid>
        )}
      </Grid>
    </Popup>
  );

  return (
    <Marker icon={renderIcon} zIndex={1} position={position} {...props}>
      {probeInfo}
    </Marker>
  );
}

ProbeMarker.propTypes = {
  probe: PropTypes.object.isRequired,
  missingReadings: PropTypes.bool,
  selected: PropTypes.bool,
  telemetry: PropTypes.object,
  events: PropTypes.array,
  position: PropTypes.object,
};

ProbeMarker.defaultProps = {
  telemetry: undefined,
  missingReadings: false,
  selected: false,
  position: undefined,
};
